import { Injectable } from '@angular/core';

import { SalteAuth } from '@salte-auth/salte-auth';
import { camelCase, some } from 'lodash-es';

import { ConfigService, Config } from '../services/config.service';
import { UserRole } from './user-role.type';
import { jwtDecode } from 'jwt-decode';
import { AuthService as KongAuth0, User } from '@auth0/auth0-angular';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  provider: any;
  appConfig: Config;
  error: any;
  authenticating = true;
  modalConfig = {
    size: 'sm',
    keyboard: false,
    backdrop: 'static',
    centered: true
  };

 private initialized = false;
  private userSubject = new BehaviorSubject<User | null>(null);
  constructor(private configService: ConfigService, public kongAuth0: KongAuth0) {
    this.appConfig = configService.config;
    this.startSessionCheck();
  }

  get hasAccess(): boolean {
    return this.hasRole(UserRole.User);
  }

  get isLoggedIn(): boolean {
    return !!(this.user && !this.isExpired);
  }

  get isAdmin(): boolean {
    return this.hasRole('admin');
  }

  get isAgentAdmin(): boolean {
    return this.hasRole(UserRole.AgentAdmin);
  }

  get isInternalAdmin(): boolean {
    return this.hasRole(UserRole.Admin);
  }

  get isAgent(): boolean {
    return this.hasRole(UserRole.Agent);
  }

  get isInternal(): boolean {
    return this.hasRole(UserRole.Internal);
  }

  get isExpired(): boolean {
    // Replace this.provider.idToken.expired with actual expiration check
    const token = localStorage.getItem('Auth0id_token');
    if (token) {
      const decoded: any = jwtDecode(token);
      return decoded.exp < Date.now() / 1000;
    }
    return true;
  }

  get isLoading (): any{
   return  this.kongAuth0.isLoading$;
  };

   get user$() {
    return this.userSubject.asObservable();
  }

  get user() {
    const token = localStorage.getItem('Auth0id_token');
    this.provider = token ? jwtDecode(token):null;
    return this.provider;
  }

  get roles(): string[] {
    const accessToken = localStorage.getItem('Auth0access_token');
      const decodedToken: any = accessToken ? jwtDecode(accessToken) : null;
      const roles = decodedToken?.['user-permissions'] || [];
      return roles ;
    }

  get rolesAsDescriptions(): string[] {
  const roles: string[] = [];
  if (!this.roles) {
    return roles;
  }
  this.roles.forEach(role => {
    if (!role.startsWith('agencyCode:')) {
      roles.push(this.roleDescription(role as UserRole));
    }
  });
  return roles;
}

//  get isAuthenticated (): any{
//    return  this.kongAuth0.isAuthenticated$;
//   };

  roleDescription(role: UserRole): string {
    switch (role) {
      case UserRole.User:
        return this.isInternal ? 'Internal User' : 'Standard User';
      case UserRole.Agent:
        return this.isInternal ? 'Internal Agent' : 'Agent';
      case UserRole.Admin:
        return this.isInternal ? 'Internal Admin' : 'Admin';
      case UserRole.AgentAdmin:
        return this.isInternal ? 'Internal Agent Admin' : 'Agent Admin';
      case UserRole.Internal:
        return this.isInternal ? 'Internal User' : 'Standard User';
    }
    return role;
  }

  login() {
    this.kongAuth0.isLoading$.subscribe(isLoading => {
      if (!isLoading) {
        this.kongAuth0.isAuthenticated$.subscribe(isAuthenticated => {
          if (!isAuthenticated) {
            this.authenticating = true;
            this.Auth0login();
          } else {
            this.handleSuccess();
            //this.getTokens();
          }
        });
      }
    });
  }

  async Auth0login() {
    try {
      await this.kongAuth0.loginWithRedirect();

    } catch (error) {
      console.error('Error logging in:', error);
    }
  }

  logout(): void {
    this.authenticating = true;
    localStorage.clear();
    this.kongAuth0.logout({
      logoutParams: {
        returnTo: this.configService.config.Auth0redirect_uri
      }
    });
  }

  handleError(error) {
    if (error) {
      this.error = error;
      throw new Error(error);
    }
    this.handleComplete();
  }

  startSessionCheck() {
    // Set an interval to check the session every 30 minutes (1800000 milliseconds)
    setInterval(() => {
      if (this.isExpired) {
        this.kongAuth0.logout();
        window.location.href = window.location.origin;
      }
    }, 1800000); // 30 minutes
  }

  // isAuthenticated() {
  //   return this.kongAuth0.isAuthenticated$;
  // }

 async getTokens(): Promise<void> {
  const existingIdToken = localStorage.getItem('Auth0id_token');

  try {
    // ID Token
    if (!existingIdToken || this.isExpired) {
      const claims = await firstValueFrom(this.kongAuth0.idTokenClaims$);
      if (claims) {
        const idToken = claims.__raw;
        localStorage.setItem('Auth0id_token', idToken);
        this.decodeAndStoreUserInfo(idToken);
      }

    // Access Token
      const accessToken = await firstValueFrom(this.kongAuth0.getAccessTokenSilently());
      if (accessToken) {
        localStorage.setItem('Auth0access_token', accessToken);
        this.decodeAndStoreAccessTokenUserInfo(accessToken);
      }
    }
  } catch (err) {
    console.error('Error fetching tokens', err);
  }
}

 async initAuth(): Promise<void> {
  if (this.initialized) {
    return;
  }
  this.initialized = true;

  try {
    const isAuth = await firstValueFrom(this.kongAuth0.isAuthenticated$);
    if (isAuth) {
      const user = await firstValueFrom(this.kongAuth0.user$);
      this.userSubject.next(user);

      // Wait for tokens before allowing API calls
      await this.getTokens();
    }
  } catch (err) {
    console.error('Auth init failed', err);
  }
}



  async isAuthenticated(): Promise<boolean> {
    return await firstValueFrom(this.kongAuth0.isAuthenticated$);
  }

  private handleSuccess() {
    this.error = null;
    this.handleComplete();
  }

  private handleComplete() {
    document.body.setAttribute('authenticated', '');
  }

  private handleLogout() {
    this.authenticating = true;
    document.body.removeAttribute('authenticated');
  }

  private hasRole(role): boolean {
    return some(this.roles, (aRole) => camelCase(aRole).toUpperCase().indexOf(camelCase(role).toUpperCase()) > -1);
  }

  private isString(value: any): value is string {
    return typeof value === 'string';
  }

  private decodeAndStoreUserInfo(token: string) {
    try {
      const decoded: any = jwtDecode(token);
      const user_type: any = decoded?.user_type =='internal' ? true : false;
      localStorage.setItem('Authuser_type', user_type);
    } catch (error) {
      console.error('Error decoding token:', error);
    }
  }

  private decodeAndStoreAccessTokenUserInfo(token: string) {
    try {
      const decodedToken: any = jwtDecode(token);
      const loginUserDetail = {
        email: decodedToken?.email || null,
        given_name: decodedToken?.given_name || null,
        family_name: decodedToken?.family_name || null,
        company: decodedToken,
      };


    } catch (error) {
      console.error('Error decoding access token:', error);
    }
  }
}